/**
 * 一覧の絞りと合計（#302、2026-11-04 経理担当の指摘 2本目、docs/94 B-3 の追記・Figma「一覧の見直し（11/4）」①）。
 * 見積書・請求書・{deliveryNote}・入金の一覧の表のカード（.lcard）の中、タブの段と表の間に1段：
 *   期間［すべて／今月／先月／今期／期間指定 ▾］（期間指定は開始日・終了日の欄）・{customer}［すべて ▾］（押すと取引先検索のドロアー）｜右端に絞った結果の「n件・税抜・税込」
 * 絞りは URL に持つ（?period=・from・to・client。書類を開いて戻っても残る）。期間の計算・{customer}の名前・検索の決まりは contract の listFilter.ts 1か所
 */
import { LIST_MSG, LIST_PERIOD_LABEL, ListPeriod, ListSendFilter, SEND_STATE_LABEL, listNameOf, listPeriodRange, type Partner } from "@genbaryoku/contract";
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { api } from "../api.js";
import { useMe } from "../auth.js";
import { useLabels } from "../labels.js";
import { todayJst, yen } from "../lib/num.js";
import { DateInput } from "./DateInput.jsx";
import { PartnerPickerDrawer } from "./PartnerPickerDrawer.jsx";

export type ListFilters = {
  period: ListPeriod;
  /** 期間指定の開始日・終了日（URL の値。空なら ""） */
  from: string;
  to: string;
  /** {customer}の id（空なら絞らない） */
  client: string;
  /** 送付の絞り（#303 B3。URL の ?send=。請求書の一覧だけが出す） */
  send: ListSendFilter;
  /** 期間の範囲（両端を含む。すべてなら両方 null） */
  range: { from: string | null; to: string | null };
  /** 絞っているか（空のときの文言を変える） */
  active: boolean;
  setPeriod: (p: ListPeriod) => void;
  setRange: (from: string, to: string) => void;
  setClient: (id: string) => void;
  setSend: (v: ListSendFilter) => void;
};

/** URL の ?period=・from・to・client を読む（ほかの値＝タブ・f は残す） */
export function useListFilters(): ListFilters {
  const [sp, setSp] = useSearchParams();
  const me = useMe().data;
  const p = ListPeriod.safeParse(sp.get("period"));
  const period: ListPeriod = p.success ? p.data : "all";
  const from = sp.get("from") ?? "";
  const to = sp.get("to") ?? "";
  const client = sp.get("client") ?? "";
  const sv = ListSendFilter.safeParse(sp.get("send"));
  const send: ListSendFilter = sv.success ? sv.data : "all";
  const range = listPeriodRange(period, todayJst(), me?.tenant.fiscalStartMonth ?? 4, from, to);
  const put = (f: (n: URLSearchParams) => void) =>
    setSp(
      (cur) => {
        const n = new URLSearchParams(cur);
        f(n);
        return n;
      },
      { replace: true },
    );
  return {
    period,
    from,
    to,
    client,
    send,
    range,
    active: period !== "all" || !!client || send !== "all",
    setPeriod: (k) =>
      put((n) => {
        if (k === "all") n.delete("period");
        else n.set("period", k);
        if (k !== "range") {
          n.delete("from");
          n.delete("to");
        }
      }),
    setRange: (f, tt) =>
      put((n) => {
        n.set("period", "range");
        if (f) n.set("from", f);
        else n.delete("from");
        if (tt) n.set("to", tt);
        else n.delete("to");
      }),
    setClient: (id) =>
      put((n) => {
        if (id) n.set("client", id);
        else n.delete("client");
      }),
    setSend: (v) =>
      put((n) => {
        if (v === "all") n.delete("send");
        else n.set("send", v);
      }),
  };
}

/** 絞った結果の合計（右端）。入金は 件数・入金額・未消込額 */
export type ListSum = { count: number; subtotal?: number; total?: number; balance?: number; amount?: number; unallocated?: number };

export function ListFilterBar({
  f,
  clientName,
  sum,
  sendFilter = false,
  testId = "lf",
}: {
  f: ListFilters;
  /** 選んでいる{customer}の名前（一覧の行から分かれば。無ければ API から読む） */
  clientName?: string | undefined;
  sum: ListSum | null;
  /** 送付の絞り（未送付／送付済／取得済。#303 B3＝請求書の一覧） */
  sendFilter?: boolean;
  testId?: string;
}) {
  const L = useLabels();
  const [open, setOpen] = useState(false);
  const known = clientName ?? "";
  const one = useQuery({
    queryKey: ["partner", f.client],
    queryFn: () => api<Partner>("GET", `/partners/${f.client}`),
    enabled: !!f.client && !known,
    staleTime: 60_000,
  });
  const name = f.client ? known || (one.data ? listNameOf({ name: one.data.name, shortName: one.data.shortName }) : "…") : "";
  const yenOf = (label: string, v: number | undefined, strong = false): ReactNode =>
    v == null ? null : (
      <span className={strong ? "lf-strong" : undefined}>
        {label} <span className="num">{yen(v)}</span>円
      </span>
    );
  return (
    <div className="lfbar" data-testid={testId}>
      <span className="lf-filters">
        {/* 名前と欄は組で折り返す（#303 A1：名前だけが行の終わりに残らない） */}
        <span className="lf-grp">
          <label className="lf-l" htmlFor={`${testId}-period`}>
            {LIST_MSG.period}
          </label>
          <span className="lf-sel">
            <select id={`${testId}-period`} value={f.period} onChange={(e) => f.setPeriod(e.target.value as ListPeriod)} data-testid={`${testId}-period`}>
              {ListPeriod.options.map((k) => (
                <option key={k} value={k}>
                  {LIST_PERIOD_LABEL[k]}
                </option>
              ))}
            </select>
          </span>
          {f.period === "range" ? (
            <span className="lf-range">
              <DateInput value={f.from} onChange={(v) => f.setRange(v, f.to)} label={LIST_MSG.periodFrom} ariaLabel={LIST_MSG.periodFrom} testId={`${testId}-from`} />
              <span aria-hidden="true">〜</span>
              <DateInput value={f.to} onChange={(v) => f.setRange(f.from, v)} label={LIST_MSG.periodTo} ariaLabel={LIST_MSG.periodTo} testId={`${testId}-to`} />
            </span>
          ) : null}
        </span>
        <span className="lf-grp">
          <span className="lf-l" id={`${testId}-client-l`}>
            {L.customer}
          </span>
          <span className="lf-client">
            <button type="button" className={"lf-pick" + (f.client ? " is-on" : "")} aria-labelledby={`${testId}-client-l ${testId}-client`} onClick={() => setOpen(true)} data-testid={`${testId}-client`} title={f.client ? name : undefined}>
              <span id={`${testId}-client`} className="lf-pickname">
                {f.client ? name : LIST_MSG.customerAll}
              </span>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M7 10l5 5 5-5" />
              </svg>
            </button>
            {f.client ? (
              <button type="button" className="lf-clear" aria-label={`${L.customer}の${LIST_MSG.customerClear}`} title={LIST_MSG.customerClear} onClick={() => f.setClient("")} data-testid={`${testId}-client-clear`}>
                ×
              </button>
            ) : null}
          </span>
        </span>
        {sendFilter ? (
          <span className="lf-grp">
            <label className="lf-l" htmlFor={`${testId}-send`}>
              {LIST_MSG.sendFilter}
            </label>
            <span className="lf-sel is-send">
              <select id={`${testId}-send`} value={f.send} onChange={(e) => f.setSend(e.target.value as ListSendFilter)} data-testid={`${testId}-send`}>
                {ListSendFilter.options.map((k) => (
                  <option key={k} value={k}>
                    {k === "all" ? LIST_MSG.sendAll : SEND_STATE_LABEL[k]}
                  </option>
                ))}
              </select>
            </span>
          </span>
        ) : null}
      </span>
      {sum ? (
        <span className="lf-sum" data-testid={`${testId}-sum`}>
          <span className="lf-count">{LIST_MSG.count(sum.count)}</span>
          {yenOf(LIST_MSG.amount, sum.amount, true)}
          {yenOf(LIST_MSG.unallocated, sum.unallocated)}
          {yenOf(LIST_MSG.subtotal, sum.subtotal)}
          {yenOf(LIST_MSG.total, sum.total, true)}
          {yenOf(LIST_MSG.balance, sum.balance)}
        </span>
      ) : null}
      <PartnerPickerDrawer open={open} role="customer" onClose={() => setOpen(false)} onPick={(p) => f.setClient(p.id)} />
    </div>
  );
}
